// Interview Prep's shared topic list. Setup ratings, skills, role profiles,
// the problem bank (problems.json, from backend/scripts/fetch_neetcode.py)
// and the planner all use these ids, so they must stay in sync with that
// script's TOPICS map.

export type CodingTopicId =
  | "arrays-hashing"
  | "two-pointers"
  | "sliding-window"
  | "stack"
  | "binary-search"
  | "linked-list"
  | "trees"
  | "tries"
  | "heap"
  | "backtracking"
  | "graphs"
  | "advanced-graphs"
  | "dp-1d"
  | "dp-2d"
  | "greedy"
  | "intervals"
  | "math-geometry"
  | "bit-manipulation";

/** Behavioral themes: a story in the story bank covers one */
export type StoryTopicId = "story-conflict" | "story-failure" | "story-leadership" | "story-impact" | "story-teamwork";

export type TopicId = CodingTopicId | StoryTopicId;

export type Topic = {
  id: TopicId;
  name: string;
  track: "coding" | "behavioral";
  /** Setup shows coding topics in these groups */
  group?: "Basics" | "Trees & graphs" | "Techniques";
};

export const CODING_TOPICS: Topic[] = [
  { id: "arrays-hashing", name: "Arrays & Hashing", track: "coding", group: "Basics" },
  { id: "two-pointers", name: "Two Pointers", track: "coding", group: "Basics" },
  { id: "sliding-window", name: "Sliding Window", track: "coding", group: "Basics" },
  { id: "stack", name: "Stack", track: "coding", group: "Basics" },
  { id: "binary-search", name: "Binary Search", track: "coding", group: "Basics" },
  { id: "linked-list", name: "Linked List", track: "coding", group: "Basics" },
  { id: "trees", name: "Trees", track: "coding", group: "Trees & graphs" },
  { id: "tries", name: "Tries", track: "coding", group: "Trees & graphs" },
  { id: "heap", name: "Heap / Priority Queue", track: "coding", group: "Trees & graphs" },
  { id: "graphs", name: "Graphs", track: "coding", group: "Trees & graphs" },
  { id: "advanced-graphs", name: "Advanced Graphs", track: "coding", group: "Trees & graphs" },
  { id: "backtracking", name: "Backtracking", track: "coding", group: "Techniques" },
  { id: "dp-1d", name: "1-D Dynamic Programming", track: "coding", group: "Techniques" },
  { id: "dp-2d", name: "2-D Dynamic Programming", track: "coding", group: "Techniques" },
  { id: "greedy", name: "Greedy", track: "coding", group: "Techniques" },
  { id: "intervals", name: "Intervals", track: "coding", group: "Techniques" },
  { id: "math-geometry", name: "Math & Geometry", track: "coding", group: "Techniques" },
  { id: "bit-manipulation", name: "Bit Manipulation", track: "coding", group: "Techniques" },
];

/**
 * What to be ready in before starting a topic, after the NeetCode roadmap.
 * The planner holds a topic back while the user is weak in it and in one of
 * these, or in any topic further back the chain.
 */
export const PREREQUISITES: Partial<Record<CodingTopicId, CodingTopicId[]>> = {
  "two-pointers": ["arrays-hashing"],
  stack: ["arrays-hashing"],
  "binary-search": ["two-pointers"],
  "sliding-window": ["two-pointers"],
  "linked-list": ["two-pointers"],
  trees: ["binary-search", "linked-list"],
  tries: ["trees"],
  heap: ["trees"],
  backtracking: ["trees"],
  graphs: ["backtracking"],
  "dp-1d": ["backtracking"],
  intervals: ["heap"],
  greedy: ["heap"],
  "advanced-graphs": ["graphs", "heap"],
  "dp-2d": ["graphs", "dp-1d"],
  "bit-manipulation": ["dp-1d"],
  "math-geometry": ["bit-manipulation"],
};

export const STORY_TOPICS: Topic[] = [
  { id: "story-conflict", name: "Conflict", track: "behavioral" },
  { id: "story-failure", name: "Failure", track: "behavioral" },
  { id: "story-leadership", name: "Leadership", track: "behavioral" },
  { id: "story-impact", name: "Impact", track: "behavioral" },
  { id: "story-teamwork", name: "Teamwork", track: "behavioral" },
];

export const TOPICS: Topic[] = [...CODING_TOPICS, ...STORY_TOPICS];

const BY_ID = new Map(TOPICS.map((t) => [t.id, t]));

export function topicName(id: TopicId): string {
  return BY_ID.get(id)?.name ?? id;
}

export type Difficulty = "Easy" | "Medium" | "Hard";

/** One entry of problems.json; links out to leetcode.com/problems/{slug} */
export type Problem = {
  title: string;
  slug: string;
  topic: CodingTopicId;
  difficulty: Difficulty;
  nc150?: boolean;
  nc250?: boolean;
  blind75?: boolean;
  /** Needs LeetCode Premium to open */
  premium?: boolean;
};

export const leetcodeUrl = (slug: string) => `https://leetcode.com/problems/${slug}/`;
